import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for Firebase's first onAuthStateChanged to complete before checking auth state.
  // Without this, BehaviorSubject emits its initial `false` immediately, causing a
  // redirect to login even when the user is already logged in (race condition on refresh).
  await firstValueFrom(authService.authInitialized$.pipe(take(1)));

  const isAuthenticated = await firstValueFrom(
    authService.isAuthenticated().pipe(take(1))
  );

  if (isAuthenticated) {
    // Anonymous guests that are NOT an intentional demo session (e.g. a signed-out
    // visitor who got an anon token from a room link) must not roam protected
    // routes — they have no user doc. Send them to register/login instead of the
    // verify-email screen (which is only for real, unverified accounts).
    if (authService.isAnonymous() && !authService.isDemoSession) {
      return router.createUrlTree(['/pages/auth/login'], {
        queryParams: { returnUrl: state.url }
      });
    }
    if (!authService.isDemoSession && !authService.isEmailVerified()) {
      return router.createUrlTree(['/pages/auth/verify-email'], {
        queryParams: { returnUrl: state.url }
      });
    }
    // A valid Firebase token is not enough: protected routes read user-scoped
    // data, which Firestore rules deny without a real profile doc. A session
    // whose users/{uid} doc is missing (deleted doc, stale token) would render
    // an empty "demo-looking" home with all reads denied. Treat as logged out.
    if (!authService.isDemoSession && !authService.currentUserValue) {
      return router.createUrlTree(['/pages/auth/login'], {
        queryParams: { returnUrl: state.url }
      });
    }
    return true;
  }

  // Content (quizzes, videos, leaders, stories) now requires a real account —
  // anonymous guest sessions can no longer read it. Send everyone unauthenticated
  // to login; they return to the requested route after signing in.
  return router.createUrlTree(['/pages/auth/login'], {
    queryParams: { returnUrl: state.url }
  });
};
